import { useEffect, useState } from "react";
import { toast } from "react-toastify";

import {
  useClientQuery,
  useUpdateClientMutation,
} from "@graphql/clients/clients.g";

import { Button } from "@design-system/Button/Button";
import { Card } from "@design-system/Card/Card";
import { FieldGroup } from "@design-system/FieldGroup/FieldGroup";
import { Text } from "@design-system/Text/Text";
import { TextField } from "@design-system/TextField/TextField";

type Props = {
  clientId: string;
};

const toInputValue = (days: number | null | undefined) =>
  days == null ? "" : String(days);

export const InactivityThresholdCard = ({ clientId }: Props) => {
  const clientQuery = useClientQuery({ clientId });
  const updateClientMutation = useUpdateClientMutation();

  const serverDays = clientQuery.data?.client?.inactivityDays;
  const [days, setDays] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    if (!hasInitialized && clientQuery.data?.client) {
      setDays(toInputValue(clientQuery.data.client.inactivityDays));
      setHasInitialized(true);
    }
  }, [clientQuery.data?.client, hasInitialized]);

  const parsedDays = days.trim() === "" ? null : Number(days);
  const isInvalidNumber =
    parsedDays !== null && (!Number.isInteger(parsedDays) || parsedDays < 1);
  const isDirty = toInputValue(serverDays) !== days.trim();

  const handleUpdate = async () => {
    if (isInvalidNumber) {
      setError("Enter a whole number of at least 1, or leave empty to disable.");
      return;
    }

    setError(undefined);

    try {
      const data = await updateClientMutation.mutateAsync({
        input: {
          id: clientId,
          inactivityDays: parsedDays,
        },
      });

      if (data.updateClient?.errors?.length) {
        const firstError = data.updateClient.errors[0]?.message;
        setError(firstError || "Unable to save inactivity window.");
        return;
      }

      toast.success("Inactivity window updated");
      const saved = data.updateClient?.client?.inactivityDays;
      setDays(toInputValue(saved));
    } catch {
      toast.error("Unable to save inactivity window");
    }
  };

  const isSaving = updateClientMutation.isPending;

  return (
    <Card>
      <Card.Header>
        <Card.Header.Title>User inactivity</Card.Header.Title>
      </Card.Header>
      <Card.Body gap={12}>
        <Text size={12} color="textSecondary">
          New tenants start at 30 days. Notify once when a user who
          previously had activity has no recorded browser events for this many
          days. Leave empty to disable.
        </Text>
        <FieldGroup label="Inactivity window (days)" error={error}>
          <TextField
            type="number"
            min={1}
            step={1}
            inputMode="numeric"
            value={days}
            placeholder="30"
            onChange={(event) => {
              setDays(event.target.value);
              setError(undefined);
            }}
            disabled={isSaving}
          />
        </FieldGroup>
        <Button
          isLoading={isSaving}
          disabled={!isDirty || isSaving || isInvalidNumber}
          onClick={handleUpdate}
        >
          Update
        </Button>
      </Card.Body>
    </Card>
  );
};
